"use client";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import Select, { MultiValue, SingleValue, ActionMeta } from "react-select";
import { ErrorMessage, FormikProps } from "formik";

interface SelectOption {
  value: string;
  label: string;
}

interface DataItem {
  id: number | string;
  name?: string;
  title?: string;
}

interface CustomSelectLocalProps {
  FormikProps: FormikProps<any>;
  name: string;
  placeholder: string;
  data: DataItem[];
  label?: string;
  onChangeCallback?: (selectedOption: any) => void;
  className?: string;
  twoStyle?: boolean;
  classNameSelectWrap?: string;
  isMulti?: boolean;
  value?: any;
}

const CustomSelectLocal: React.FC<CustomSelectLocalProps> = ({
  FormikProps,
  name,
  placeholder,
  data,
  label,
  onChangeCallback,
  className = "",
  twoStyle = false,
  classNameSelectWrap = "",
  isMulti = false,
  value,
}) => {
  const [selected, setSelected] = useState<
    MultiValue<SelectOption> | SingleValue<SelectOption>
  >(isMulti ? [] : null);

  // Memoized options to prevent unnecessary re-renders
  const options = useMemo<SelectOption[]>(() => {
    if (!Array.isArray(data)) return [];

    return data.map((item) => ({
      value: item.id.toString(),
      label: item.name || item.title || "",
    }));
  }, [data]);

  // Find option by value helper
  const findOptionByValue = useCallback(
    (searchValue: any): SelectOption | undefined => {
      if (!searchValue) return undefined;
      return options.find((option) => option.value === searchValue.toString());
    },
    [options]
  );

  // Initialize selected value from Formik
  useEffect(() => {
    const formikValue = FormikProps.values[name];

    if (isMulti && Array.isArray(formikValue)) {
      const selectedOptions = formikValue
        .map((val) => findOptionByValue(val))
        .filter(Boolean) as SelectOption[];
      setSelected(selectedOptions);
    } else if (!isMulti && formikValue) {
      const selectedOption = findOptionByValue(formikValue);
      setSelected(selectedOption || null);
    } else {
      setSelected(isMulti ? [] : null);
    }
  }, [FormikProps.values[name], findOptionByValue, isMulti, name]);

  // Handle external value prop
  useEffect(() => {
    if (value !== undefined) {
      if (isMulti && Array.isArray(value)) {
        const selectedOptions = value
          .map((val) => findOptionByValue(val))
          .filter(Boolean) as SelectOption[];
        setSelected(selectedOptions);
      } else if (!isMulti) {
        const selectedOption = findOptionByValue(value);
        setSelected(selectedOption || null);
      }
    }
  }, [value, findOptionByValue, isMulti]);

  // Handle selection change - Fixed typing
  const handleChange = useCallback(
    (
      newValue: MultiValue<SelectOption> | SingleValue<SelectOption>,
      actionMeta: ActionMeta<SelectOption>
    ) => {
      setSelected(newValue);

      if (isMulti && Array.isArray(newValue)) {
        const values = newValue.map((option) => option.value);
        FormikProps.setFieldValue(name, values);
        onChangeCallback?.(values);
      } else if (!isMulti) {
        const singleOption = newValue as SingleValue<SelectOption>;
        const value = singleOption?.value || "";
        FormikProps.setFieldValue(name, value);
        onChangeCallback?.(value || null);
      }
    },
    [FormikProps, name, onChangeCallback, isMulti]
  );

  // Memoized custom styles
  const getCustomStyles = useMemo(() => {
    const hasError = FormikProps?.errors[name] && FormikProps?.touched[name];
    const hasValue = FormikProps?.values[name];
    const backgroundColor = twoStyle ? "#232c37" : "#333e4b";

    return {
      control: (provided: any, state: any) => ({
        ...provided,
        height: "60px",
        borderColor: hasError ? "red" : hasValue ? "#37b2f0" : "#333e4b",
        "&:hover": {
          borderColor: state.isFocused ? "#37b2f0" : provided.borderColor,
        },
        backgroundColor,
        color: selected ? "white" : provided.color,
      }),
      input: (provided: any) => ({
        ...provided,
        color: "#fff",
      }),
      placeholder: (provided: any) => ({
        ...provided,
        color: selected ? "#fff" : provided.color,
      }),
      singleValue: (provided: any) => ({
        ...provided,
        color: "#fff",
      }),
      multiValue: (provided: any) => ({
        ...provided,
        backgroundColor: "#37b2f0",
        color: "#000",
      }),
      multiValueLabel: (provided: any) => ({
        ...provided,
        color: "#000",
      }),
      menu: (provided: any) => ({
        ...provided,
        zIndex: 9999,
        marginTop: 0,
        position: "absolute",
      }),
      menuPortal: (base: any) => ({
        ...base,
        zIndex: 9999,
      }),
    };
  }, [
    FormikProps?.errors,
    FormikProps?.touched,
    FormikProps?.values,
    name,
    selected,
    twoStyle,
  ]);

  return (
    <div className={`w-full mb-2 ${className}`}>
      {label && (
        <label htmlFor={name} className="text-16 text-white mb-2 block">
          {label}
        </label>
      )}
      <div className={classNameSelectWrap}>
        <Select
          id={name}
          name={name}
          isMulti={isMulti}
          styles={getCustomStyles}
          options={options}
          value={selected}
          onChange={handleChange}
          placeholder={placeholder}
          isClearable
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : undefined
          }
          menuPosition="fixed"
          menuPlacement="auto"
        />
      </div>
      <ErrorMessage
        name={name}
        component="div"
        className="text-[#F75555] text-sm mt-1"
      />
    </div>
  );
};

export default CustomSelectLocal;
